import React, {useState} from 'react';

import {Assertion} from '@udemy/badging';
import {useI18n, LocalizedHtml} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Button} from '@udemy/react-core-components';
import {FooterButtons, Modal} from '@udemy/react-dialog-components';
import {Dropdown} from '@udemy/react-menu-components';

import styles from './share-url-menu-item.module.less';

const ShareUrlModal: React.FC<{isOpen: boolean; onClose: () => void; assertion: Assertion}> = ({
    isOpen,
    onClose,
    assertion,
}) => {
    const {gettext} = useI18n();

    const handleDownloadBadge = async () => {
        try {
            const imageUrl = assertion.uploadedImageUrl || assertion.badgeClass?.image?.id;
            const imageName = assertion.badgeClass?.name || 'badge';
            const response = await fetch(imageUrl);

            if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.status}`);
            }
            const imageData = await response.blob();
            const blobUrl = URL.createObjectURL(imageData);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `${imageName}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            // open in new tab if blob download fails
            const imageUrl = assertion.uploadedImageUrl || assertion.badgeClass?.image?.id;
            if (imageUrl) {
                window.open(imageUrl, '_blank');
            }
        }
    };

    const handleSupportArticleClick = () => {
        window.open(
            'https://support.udemy.com/hc/en-us/articles/29195420837143-Downloading-a-Badge-on-Udemy',
            '_blank',
            'noopener,noreferrer',
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={gettext('Share Badge URL')}>
            <div className={styles['modal-content']}>
                <div className={styles['modal-paragraph']}>
                    <LocalizedHtml
                        html={gettext(
                            'To share your badge publicly, you will need to first <a class="download-link">download your badge</a> and import it to your badge wallet of choice. From there, you will have an option to generate and copy a publicly shareable URL.',
                        )}
                        interpolate={{
                            'download-link': (
                                <button
                                    type="button"
                                    className={styles['modal-link']}
                                    onClick={handleDownloadBadge}
                                />
                            ),
                        }}
                    />
                </div>

                <div className={styles['modal-paragraph']}>
                    <LocalizedHtml
                        html={gettext(
                            'As Udemy is not currently a public Open Badge hosting platform, we recommend you download your badges to your own wallet for sharing and safe keeping.',
                        )}
                        interpolate={{}}
                    />
                </div>

                <div>
                    <LocalizedHtml
                        html={gettext(
                            'To learn more, read <a class="support-link">this support article</a>.',
                        )}
                        interpolate={{
                            'support-link': (
                                <button
                                    type="button"
                                    className={styles['modal-link']}
                                    onClick={handleSupportArticleClick}
                                />
                            ),
                        }}
                    />
                </div>
            </div>

            <FooterButtons>
                <Button onClick={onClose} udStyle="primary">
                    {gettext('OK')}
                </Button>
            </FooterButtons>
        </Modal>
    );
};

export const ShareUrlMenuItem: React.FC<{assertion: Assertion}> = ({assertion}) => {
    const {gettext} = useI18n();
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <>
            <Dropdown.MenuItem onClick={() => setIsModalOpen(true)} data-testid="share-url-item">
                <span className={styles['menu-item']}>
                    <Icon glyph="share" size="small" color="inherit" label="" aria-hidden="true" />
                    <span>{gettext('Share URL')}</span>
                </span>
            </Dropdown.MenuItem>
            <ShareUrlModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                assertion={assertion}
            />
        </>
    );
};
